import * as z from "zod";

/**
 * Snapshotted on the row so a retry weeks later resubmits what was reviewed.
 * Limits mirror Telnyx's spec, whose 422 no retry can clear.
 */

export const RcsLegalEntityTypeSchema = z.enum([
  "LIMITED_LIABILITY_COMPANY",
  "SOLE_PROPRIETORSHIP",
  "PARTNERSHIP",
  "CORPORATION",
  "S_CORPORATION",
]);
export type RcsLegalEntityType = z.infer<typeof RcsLegalEntityTypeSchema>;

export const RcsOrganizationTypeSchema = z.enum([
  "PRIVATE_PROFIT",
  "PUBLIC_PROFIT",
  "NON_PROFIT",
  "GOVERNMENT",
  "UNKNOWN",
]);
export type RcsOrganizationType = z.infer<typeof RcsOrganizationTypeSchema>;

export const RcsAgentUseCaseSchema = z.enum([
  "MULTI_USE",
  "PROMOTIONAL",
  "TRANSACTIONAL",
  "OTP",
]);
export type RcsAgentUseCase = z.infer<typeof RcsAgentUseCaseSchema>;

/** Telnyx requires the scheme; a bare domain is rejected. */
const HttpsUrlSchema = z
  .string()
  .trim()
  .regex(/^https?:\/\//i, "Must be an http(s) URL");

const RcsBrandAddressSchema = z.object({
  line1: z.string().trim().min(1).max(255),
  line2: z.string().trim().min(1).max(255).optional(),
  city: z.string().trim().min(1).max(100),
  /** State/province. */
  administrativeArea: z.string().trim().min(1).max(100),
  postalCode: z.string().trim().min(1).max(32),
  countryCode: z
    .string()
    .trim()
    .regex(/^[A-Z]{2}$/),
});

const RcsBrandContactSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  title: z.string().trim().min(1).max(100).optional(),
  email: z.string().trim().email().max(254),
  phoneNumber: z
    .string()
    .trim()
    .regex(/^\+[1-9][0-9]{1,14}$/),
});

export const RcsBrandPayloadSchema = z
  .object({
    displayName: z.string().trim().min(1).max(100),
    legalName: z.string().trim().min(1).max(100),
    legalEntityType: RcsLegalEntityTypeSchema,
    organizationType: RcsOrganizationTypeSchema,
    websiteUrl: HttpsUrlSchema,
    /** Messaging profile the brand, and by inheritance the agent, sends under. */
    profileId: z.string().trim().max(36).optional(),
    /** Nine digits, dash optional. */
    ein: z
      .string()
      .trim()
      .regex(/^(?:[0-9]{9}|[0-9]{2}-[0-9]{7})$/),
    /** `EXCHANGE:SYMBOL`. Telnyx requires it for PUBLIC_PROFIT brands. */
    stockSymbol: z
      .string()
      .trim()
      .regex(/^\S+:\S+$/)
      .optional(),
    address: RcsBrandAddressSchema,
    contact: RcsBrandContactSchema,
  })
  .superRefine((value, ctx) => {
    // Telnyx rejects a PUBLIC_PROFIT brand with no stock symbol, and rejects the
    // key on any other organization type. Both are 422s no retry can fix.
    if (value.organizationType === "PUBLIC_PROFIT" && !value.stockSymbol) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["stockSymbol"],
        message:
          "A PUBLIC_PROFIT brand requires stockSymbol as EXCHANGE:SYMBOL",
      });
    }
    if (value.organizationType !== "PUBLIC_PROFIT" && value.stockSymbol) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["stockSymbol"],
        message: "stockSymbol is only accepted on a PUBLIC_PROFIT brand",
      });
    }
  });
export type RcsBrandPayload = z.infer<typeof RcsBrandPayloadSchema>;

export const RcsAgentBasicsPayloadSchema = z.object({
  description: z.string().trim().min(1).max(100),
  /** 224×224. */
  logoUrl: HttpsUrlSchema,
  /** 1440×448. */
  heroUrl: HttpsUrlSchema,
  brandColor: z
    .string()
    .trim()
    .regex(/^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/),
  privacyPolicyUrl: HttpsUrlSchema,
  termsAndConditionsUrl: HttpsUrlSchema,
  emailAddress: z.string().trim().email().max(254),
  emailLabel: z.string().trim().min(1).max(25),
  websiteUrl: HttpsUrlSchema.optional(),
  websiteLabel: z.string().trim().min(1).max(25).optional(),
});
export type RcsAgentBasicsPayload = z.infer<typeof RcsAgentBasicsPayloadSchema>;

export const RcsAgentPayloadSchema = z.object({
  displayName: z.string().trim().min(1).max(40),
  useCase: RcsAgentUseCaseSchema,
  basics: RcsAgentBasicsPayloadSchema,
});
export type RcsAgentPayload = z.infer<typeof RcsAgentPayloadSchema>;

export const RcsInteractionTypeSchema = z.enum([
  "TRANSACTIONAL_UPDATES",
  "CUSTOMER_SUPPORT",
  "LOYALTY_OR_REWARD",
  "MARKETING_OR_PROMOTIONAL",
  "ACCOUNT_ALERTS",
  "TWO_WAY_CONVERSATION",
  "OTHER",
]);

export const RcsOptInMethodTypeSchema = z.enum([
  "SMS",
  "WEBSITE",
  "MOBILE_APP",
  "QR_CODE",
  "SALE_POINT",
  "OTHER",
]);

const LongTextSchema = z.string().trim().min(1).max(2000);

/**
 * The consent disclosure carriers read. Telnyx conditionally requires a
 * description on `OTHER`, and the CTA url + screenshot on a `WEBSITE` opt-in.
 */
export const RcsConsentSettingsPayloadSchema = z
  .object({
    optInMethods: z
      .array(
        z.object({
          type: RcsOptInMethodTypeSchema,
          description: LongTextSchema.optional(),
        }),
      )
      .min(1),
    callToAction: LongTextSchema,
    callToActionUrl: HttpsUrlSchema.optional(),
    callToActionMediaUrl: HttpsUrlSchema.optional(),
    doubleOptIn: z.boolean(),
    doubleOptInMessage: LongTextSchema.optional(),
    optInMessage: LongTextSchema,
    helpResponse: LongTextSchema,
    optOutResponse: LongTextSchema,
  })
  .superRefine((value, ctx) => {
    for (const method of value.optInMethods) {
      if (method.type === "OTHER" && !method.description) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["optInMethods"],
          message: "An OTHER opt-in method requires a description",
        });
      }
    }
    const hasWebsite = value.optInMethods.some((m) => m.type === "WEBSITE");
    const hasMobileApp = value.optInMethods.some(
      (m) => m.type === "MOBILE_APP",
    );
    if (hasWebsite && !value.callToActionUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["callToActionUrl"],
        message: "A WEBSITE opt-in method requires callToActionUrl",
      });
    }
    if ((hasWebsite || hasMobileApp) && !value.callToActionMediaUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["callToActionMediaUrl"],
        message:
          "A WEBSITE or MOBILE_APP opt-in method requires callToActionMediaUrl",
      });
    }
    if (value.doubleOptIn && !value.doubleOptInMessage) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["doubleOptInMessage"],
        message: "doubleOptIn requires doubleOptInMessage",
      });
    }
  });
export type RcsConsentSettingsPayload = z.infer<
  typeof RcsConsentSettingsPayloadSchema
>;

export const RcsCampaignPayloadSchema = z.object({
  companyOverview: LongTextSchema,
  agentOverview: LongTextSchema.optional(),
  interactions: z
    .array(
      z.object({
        type: RcsInteractionTypeSchema,
        description: LongTextSchema.optional(),
      }),
    )
    .min(1),
  messageExamples: z.array(LongTextSchema).min(1),
  consentSettings: RcsConsentSettingsPayloadSchema,
});
export type RcsCampaignPayload = z.infer<typeof RcsCampaignPayloadSchema>;

/** How a carrier reviewer reproduces the agent. */
export const RcsTestingPayloadSchema = z.object({
  testUrl: HttpsUrlSchema,
  messageId: z.string().trim().min(1).max(255).optional(),
  additionalInformation: LongTextSchema.optional(),
});
export type RcsTestingPayload = z.infer<typeof RcsTestingPayloadSchema>;

export const RcsLaunchPayloadSchema = z.object({
  campaign: RcsCampaignPayloadSchema,
  testing: RcsTestingPayloadSchema,
});
export type RcsLaunchPayload = z.infer<typeof RcsLaunchPayloadSchema>;

/** Everything the registration workflow carries, snapshotted on the row. */
export const RcsRegistrationPayloadsSchema = z.object({
  brand: RcsBrandPayloadSchema,
  agent: RcsAgentPayloadSchema,
  launch: RcsLaunchPayloadSchema,
});
export type RcsRegistrationPayloads = z.infer<
  typeof RcsRegistrationPayloadsSchema
>;
